import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
    AuthError,
    AuthorizationResult,
    AuthState,
    isLoginResponse,
    LoginRequest,
    LoginResponse,
    SwaggerAuthSettings,
    User
} from '../models/auth.models';
import { NotificationService } from './notification.service';

/**
 * Swagger Authentication Service
 * 
 * Core service managing JWT-based authentication for Swagger UI.
 * Handles login, logout, token management, and authorization checks.
 * 
 * @example
 * ```typescript
 * // Configure on app initialization
 * authService.configure({
 *   apiUrl: 'https://api.example.com',
 *   swaggerUrl: 'https://api.example.com/swagger',
 *   loginPageUrl: 'http://localhost:4200/ui/login'
 * });
 * 
 * // Login
 * await authService.login('username', 'password');
 * 
 * // Check authentication
 * if (authService.isAuthenticated()) {
 *   // User is authenticated
 * }
 * 
 * // Logout
 * authService.logout();
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class SwaggerAuthService {
    private readonly http: HttpClient;
    private readonly router: Router;
    private readonly notificationService: NotificationService;

    private settings?: SwaggerAuthSettings;
    private authState$ = new BehaviorSubject<AuthState>({
        isAuthenticated: false,
        user: null,
        token: null,
        tokenExpires: null
    });

    // Store encrypted token in memory only (for passing between components)
    private encryptedToken: string | null = null;

    /**
     * Observable of current authentication state
     */
    readonly authState: Observable<AuthState> = this.authState$.asObservable();

    /**
     * Observable indicating if user is authenticated
     */
    readonly isAuthenticated$: Observable<boolean> = this.authState$.pipe(
        map(state => state.isAuthenticated)
    );

    /**
     * Observable of current user
     */
    readonly currentUser$: Observable<User | null> = this.authState$.pipe(
        map(state => state.user)
    );

    constructor(
        http: HttpClient,
        router: Router,
        notificationService: NotificationService
    ) {
        this.http = http;
        this.router = router;
        this.notificationService = notificationService;
    }

    /**
     * Configure the authentication service
     * 
     * Must be called before using any authentication features.
     * Typically done in APP_INITIALIZER.
     * 
     * @param settings - Partial or complete authentication settings
     */
    configure(settings: Partial<SwaggerAuthSettings>): void {
        this.settings = {
            ...this.settings,
            ...settings,
            // Ensure loginPageUrl and loginUrl are synced
            loginPageUrl: settings.loginPageUrl || settings.loginUrl,
            loginUrl: settings.loginUrl || settings.loginPageUrl || ''
        } as SwaggerAuthSettings;
    }

    /**
     * Get current authentication settings
     * 
     * @returns Current SwaggerAuthSettings configuration
     * @throws Error if settings have not been configured
     */
    getSettings(): SwaggerAuthSettings {
        if (!this.settings) {
            throw new Error('SwaggerAuthService: Settings not configured. Call configure() first.');
        }
        return this.settings;
    }

    /**
     * Authenticate user with username and password
     * 
     * @param username - User's username or email
     * @param password - User's password
     * @returns Promise resolving to login response with encrypted token
     * @throws Error if login fails
     */
    async login(username: string, password: string): Promise<LoginResponse> {
        this.ensureConfigured();

        const loginRequest: LoginRequest = { username, password };
        const url = `${this.settings!.apiUrl}${this.settings!.authEndpoint}`;

        const response = await this.http.post<LoginResponse>(url, loginRequest)
            .pipe(
                tap(res => this.handleLoginSuccess(res)),
                catchError(err => this.handleAuthError(err, 'Login failed'))
            )
            .toPromise();

        if (!response) {
            throw new Error('Login response is undefined');
        }

        return response;
    }

    /**
     * Log out current user and clear authentication state
     */
    logout(): void {
        this.clearAuthState();

        if (this.settings?.showNotifications) {
            this.notificationService.showLogoutSuccess();
        }

        this.redirectToLogin();
    }

    /**
     * Check if user is currently authenticated
     * 
     * @returns True if user has valid token in memory
     */
    isAuthenticated(): boolean {
        const state = this.authState$.value;
        return state.isAuthenticated && state.token !== null;
    }

    /**
     * Check if user has required role(s)
     * 
     * @param requiredRoles - Array of role names (user needs at least one)
     * @returns Authorization result indicating if user is authorized
     */
    hasRole(requiredRoles: string[]): AuthorizationResult {
        const user = this.authState$.value.user;

        if (!user) {
            return {
                isAuthorized: false,
                reason: 'User not authenticated'
            };
        }

        if (!requiredRoles || requiredRoles.length === 0) {
            return { isAuthorized: true };
        }

        const userRoles = user.roles || [];
        const hasRequiredRole = requiredRoles.some(role =>
            userRoles.includes(role)
        );

        if (!hasRequiredRole) {
            const missingRoles = requiredRoles.filter(role =>
                !userRoles.includes(role)
            );

            return {
                isAuthorized: false,
                reason: 'User does not have required role',
                missingRoles
            };
        }

        return { isAuthorized: true };
    }

    /**
     * Get current encrypted JWT token
     * 
     * @returns Encrypted JWT token string or null if not authenticated
     */
    getToken(): string | null {
        return this.encryptedToken;
    }

    /**
     * Get current authenticated user
     * 
     * @returns User object or null if not authenticated
     */
    getCurrentUser(): User | null {
        return this.authState$.value.user;
    }

    /**
     * Redirect to login page with return URL
     */
    redirectToLogin(): void {
        if (this.settings?.loginPageUrl) {
            const currentUrl = window.location.href;
            const returnUrl = encodeURIComponent(currentUrl);
            window.location.href = `${this.settings.loginPageUrl}?returnUrl=${returnUrl}`;
        }
    }

    /**
     * Redirect to unauthorized page
     */
    redirectToUnauthorized(): void {
        this.router.navigate(['/ui/unauthorized']);
    }

    /**
     * Handle successful login response
     * Stores encrypted token in memory and updates authentication state
     */
    private handleLoginSuccess(response: LoginResponse): void {
        if (!isLoginResponse(response)) {
            throw new Error('Invalid login response format');
        }

        const { user, token, tokenExpires } = response;
        const expiresDate = typeof tokenExpires === 'string'
            ? new Date(tokenExpires)
            : tokenExpires;

        this.encryptedToken = token;
        this.updateAuthState(true, user, 'authenticated', expiresDate);

        if (this.settings?.showNotifications) {
            this.notificationService.showLoginSuccess(user.username);
        }
    }

    /**
     * Handle authentication errors and provide user-friendly messages
     */
    private handleAuthError(error: HttpErrorResponse, context: string): Observable<never> {
        let userMessage = 'An error occurred';

        if (error.status === 401) {
            userMessage = 'Invalid username or password. Please try again.';
        } else if (error.status === 403) {
            userMessage = 'Access denied. You do not have permission to access this resource.';
        } else if (error.status === 404) {
            userMessage = 'Authentication service not found. Please contact support.';
        } else if (error.status === 500) {
            userMessage = 'Server error. Please try again later.';
        } else if (error.status === 0) {
            userMessage = 'Unable to connect to the server. Please check your network connection.';
        } else if (error.error?.message) {
            userMessage = error.error.message;
        } else if (error.message && !error.message.includes('Http failure response')) {
            userMessage = error.message;
        }

        const authError: AuthError = {
            code: error.error?.code || 'UNKNOWN_ERROR',
            message: userMessage,
            statusCode: error.status
        };

        if (this.settings?.showNotifications) {
            this.notificationService.showError(authError.message);
        }

        return throwError(() => authError);
    }

    /**
     * Update internal authentication state
     */
    private updateAuthState(
        isAuthenticated: boolean,
        user: User | null,
        token: string | null,
        tokenExpires: Date | null
    ): void {
        this.authState$.next({
            isAuthenticated,
            user,
            token,
            tokenExpires
        });
    }

    /**
     * Clear authentication state and encrypted token from memory
     */
    private clearAuthState(): void {
        this.encryptedToken = null;
        this.updateAuthState(false, null, null, null);
    }

    /**
     * Ensure service is configured before use
     */
    private ensureConfigured(): void {
        if (!this.settings) {
            throw new Error(
                'SwaggerAuthService is not configured. Call configure() first.'
            );
        }
    }
}
